import { useCallback, useMemo } from 'react';
import wallet from '~/assets/icons/wallet.svg';
import { CurrencyIcon } from '~/components/collections';
import { Button, ButtonColors, ButtonHeight } from '~/components/ui/Button';
import { Text } from '~/components/ui/Text';
import { useWalletIcon } from '~/hooks';
import { rage, useInputAction, useObservable } from '~/rage';
import {
	Currency,
	InputAction,
	ModalButtonColor,
	ModalType,
	WalletType,
} from '~/shared';
import { TuningRow } from '../../components/TuningRow';
import {
	StyledButtonContent,
	StyledButtonsContainer,
	StyledDiagButtonContainer,
	StyledPriceValue,
	StyledRow,
	StyledTable,
	StyledTuningContainer,
} from './CarMarketInfoPlayer.styled';

const CarMarketInfoPlayer = () => {
	const currentAdvert = useObservable(rage.vehicleMarket.getCurrentAdvert());

	const rubleIcon = useWalletIcon(Currency.Ruble, true);

	const vehicleBrandName = useMemo(() => {
		if (!currentAdvert) return '';
		return rage.vehicles.getVehicleBrandNameByModel(currentAdvert.vehicleModel);
	}, [currentAdvert]);

	const vehicleModelName = useMemo(() => {
		if (!currentAdvert) return '';
		return rage.vehicles.getVehicleModelNameByModel(currentAdvert.vehicleModel);
	}, [currentAdvert]);

	const governmentPrice = useMemo(() => {
		if (!currentAdvert) return 0;
		return rage.vehicles.getGovernmentPriceByModel(currentAdvert.vehicleModel);
	}, [currentAdvert]);

	useInputAction(InputAction.CommonMenuClose, () => {
		rage.vehicleMarket
			.exitMenu()
			.catch((err) => rage.notifications.showException(err));
	});

	const handleBuy = useCallback(() => {
		rage.modal.showModal(
			{
				type: ModalType.Price,
				title: 'Подтверждение',
				text: `Вы действительно хотите купить транспорт ${vehicleBrandName} ${vehicleModelName}?`,
				price: currentAdvert?.price ?? 0,
				wallet: WalletType.Cash,
				acceptButton: {
					color: ModalButtonColor.Success,
					text: 'Оплатить',
				},
				cancelButton: {
					color: ModalButtonColor.Secondary,
					text: 'Отменить',
				},
			},
			async (result) => {
				if (result.isAccepted && currentAdvert?.vehicleId) {
					await rage.vehicleMarket.buyAdvert(currentAdvert?.vehicleId);
					await rage.vehicleMarket
						.exitMenu()
						.catch((err) => rage.notifications.showException(err));
				}
			},
		);
	}, [currentAdvert, vehicleBrandName, vehicleModelName]);

	return (
		<div>
			{currentAdvert?.tuning?.length ? (
				<StyledTuningContainer>
					<TuningRow items={currentAdvert.tuning} />
				</StyledTuningContainer>
			) : null}
			<StyledTable>
				<StyledRow>
					<Text>Владелец</Text>
					<Text>{currentAdvert?.ownerNickname}</Text>
				</StyledRow>
				<StyledRow>
					<Text>Гос. стоимость</Text>
					<StyledPriceValue>
						<Text>{governmentPrice.toLocaleString('ru-RU')}</Text>
						<CurrencyIcon icon={rubleIcon} size="md" />
					</StyledPriceValue>
				</StyledRow>
				<StyledRow>
					<Text>Пробег</Text>
					<Text>{currentAdvert?.mileage.toLocaleString('ru-RU')} км.</Text>
				</StyledRow>
				<StyledRow>
					<Text>Кол-во владельцев</Text>
					<Text>{currentAdvert?.ownersCount} чел.</Text>
				</StyledRow>
			</StyledTable>
			<StyledDiagButtonContainer>
				<Button
					color={ButtonColors.Select2}
					onClick={() => {}}
					height={ButtonHeight.Height50}
					disabled
					style={{ fontSize: 16 }}
				>
					<StyledButtonContent>
						<Text>У вас нет диагностического инструмента</Text>
					</StyledButtonContent>
				</Button>
			</StyledDiagButtonContainer>
			<StyledButtonsContainer>
				<Button
					color={ButtonColors.Default}
					onClick={() => rage.vehicleMarket.exitMenu()}
					height={ButtonHeight.Height50}
				>
					<Text>Отменить</Text>
				</Button>
				<Button
					color={ButtonColors.Buy}
					onClick={handleBuy}
					height={ButtonHeight.Height50}
				>
					<StyledButtonContent>
						<img src={wallet} />
						<Text>Купить</Text>
					</StyledButtonContent>
				</Button>
			</StyledButtonsContainer>
		</div>
	);
};

export { CarMarketInfoPlayer };
